import { isPlatformBrowser } from '@angular/common';
import {
  Directive,
  ElementRef,
  inject,
  Input,
  OnChanges,
  PLATFORM_ID,
} from '@angular/core';

/**
 * Applies custom padding and a header divider to the goab-drawer.
 *
 * The drawer header padding, header border, and content vertical padding are
 * hard-coded in the goa-drawer shadow DOM with no overridable CSS token, so they
 * are set directly on the shadow root elements. Browser-only (SSR-safe) and
 * retries while the shadow content renders.
 */
@Directive({
  selector: '[customDrawer]',
  standalone: true,
})
export class CustomDrawerDirective implements OnChanges {
  @Input() open = false;
  @Input() headerPadding = '15px 25px 10px 25px';
  @Input() headerBorderBottom =
    'var(--goa-border-width-s) solid var(--goa-color-greyscale-200)';
  @Input() contentPaddingTop = '10px';
  @Input() contentPaddingBottom = '0';
  /** Left/right spacing of a bottom drawer (maps to --goa-drawer-offset). */
  @Input() offset?: string;
  /** Top border on the actions footer (v2 bottom drawer removes it by default). */
  @Input() actionsBorderTop?: string;

  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly platformId = inject(PLATFORM_ID);

  ngOnChanges(): void {
    if (this.open && isPlatformBrowser(this.platformId)) {
      this.styleDrawer();
    }
  }

  private styleDrawer(retries = 5): void {
    const drawer =
      this.el.nativeElement.querySelector<HTMLElement>('goa-drawer');
    const shadow = drawer?.shadowRoot;
    const header = shadow?.querySelector<HTMLElement>('.header');
    const content = shadow?.querySelector<HTMLElement>('.drawer-content');
    const scrollContent = shadow?.querySelector<HTMLElement>('.scroll-content');
    const actions = shadow?.querySelector<HTMLElement>('.drawer-actions');
    if (this.offset != null) {
      drawer?.style.setProperty('--goa-drawer-offset', this.offset);
    }
    if (this.actionsBorderTop != null && actions) {
      actions.style.setProperty(
        'border-top',
        this.actionsBorderTop,
        'important'
      );
    }
    if (header && content) {
      header.style.padding = this.headerPadding;
      header.style.borderBottom = this.headerBorderBottom;
      content.style.paddingTop = this.contentPaddingTop;
      content.style.paddingBottom = this.contentPaddingBottom;
      if (scrollContent) {
        scrollContent.style.paddingTop = this.contentPaddingTop;
        scrollContent.style.paddingBottom = this.contentPaddingBottom;
      }
    } else if (drawer && retries > 0) {
      requestAnimationFrame(() => this.styleDrawer(retries - 1));
    }
  }
}
